import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const BLOG_IMAGE_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360"><rect width="640" height="360" fill="#e2e8f0"/><circle cx="320" cy="150" r="48" fill="#94a3b8"/><path d="M190 280l95-95 70 70 45-45 95 70H190z" fill="#cbd5e1"/><text x="320" y="325" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" fill="#64748b">TamCare</text></svg>');

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const normalizeBlogs = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    blogID: r.blogID ?? r.BlogID ?? r.id ?? r.Id,
    title: r.title ?? r.Title ?? '',
    summary: r.summary ?? r.Summary ?? '',
    content: r.content ?? r.Content ?? '',
    image: r.image ?? r.Image ?? '',
    category: r.category ?? r.Category ?? r.CategoryName ?? r.categoryName ?? '',
    authorID: r.authorID ?? r.AuthorID ?? null,
    createdAt: r.createdAt ?? r.CreatedAt ?? r.created_at ?? null,
    status: r.status ?? r.Status ?? 1,
  })).filter((b) => b.status !== 0);
};

const BlogList = () => {
  const [internalBlogs, setInternalBlogs] = useState([]);
  const [externalBlogs, setExternalBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetchWithAuth(`${API_BASE}/api/blogs-combined`).catch(() => null);
        if (!res) { if (!aborted){ setInternalBlogs([]); setExternalBlogs([]);} return; }
        const json = await res.json().catch(() => ({}));
        const data = json?.data ?? {};
        const internal = normalizeBlogs(Array.isArray(data.internal) ? data.internal : []);
        let external = Array.isArray(data.external) ? data.external.map((r, idx) => ({
          key: r.link || r.Content || `${r.Title || 'item'}-${idx}`,
          title: r.title ?? r.Title ?? '',
          summary: r.summary ?? r.Summary ?? '',
          link: r.link ?? r.Content ?? '',
          image: r.image ?? r.Image ?? '',
          source: r.source ?? r.Source ?? '',
          domain: r.domain ?? r.Domain ?? '',
        })).filter(x => x.link) : [];
        // Fallback: if external empty, try direct external endpoint once
        if (!aborted && external.length === 0) {
          try {
            const res2 = await fetchWithAuth(`${API_BASE}/api/blogs-external`).catch(() => null);
            const json2 = await res2?.json().catch(() => ({}));
            const arr = Array.isArray(json2?.data) ? json2.data : [];
            external = arr.map((r, idx) => ({
              key: r.link || r.Content || `${r.Title || 'item'}-${idx}`,
              title: r.title ?? r.Title ?? '',
              summary: r.summary ?? r.Summary ?? '',
              link: r.link ?? r.Content ?? '',
              image: r.image ?? r.Image ?? '',
              source: r.source ?? r.Source ?? '',
              domain: r.domain ?? r.Domain ?? '',
            })).filter(x => x.link);
          } catch (_) {}
        }
        if (!aborted) { setInternalBlogs(internal); setExternalBlogs(external); }
      } catch (e) {
        if (!aborted) { setInternalBlogs([]); setExternalBlogs([]); setError(e?.message || 'Không thể tải blog.'); }
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, []);

  const resolveImg = (src) => {
    if (!src) return '';
    if (src.startsWith('http')) return src;
    if (src.startsWith('/')) return `${API_BASE}${src}`;
    return src;
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '96px 20px 48px' }}>

        {loading && <div className="tamcare-alert">Đang tải...</div>}
        {error && <div className="tamcare-alert tamcare-alert--error">{error}</div>}

        {/* Internal blogs from DB - Góc Tư Vấn TamCare */}
        <h2 style={{ color: '#2c5282', borderLeft: '6px solid #2c5282', paddingLeft: 15, margin: '40px 0 25px', fontWeight: 800 }}>⭐ Góc Tư Vấn TamCare</h2>
        <div>
          {internalBlogs.map((b) => (
            <div key={`internal-${b.blogID}`} style={{ background: '#fff', borderRadius: 20, marginBottom: 20, padding: 20, display: 'flex', gap: 20, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              {b.image && (
                <Link to={`/blogs/${b.blogID}`} style={{ display: 'block', flex: '0 0 250px' }}>
                  <img src={resolveImg(b.image)} alt={b.title} loading="lazy" decoding="async" style={{ width: 250, height: 160, objectFit: 'cover', borderRadius: 15, display: 'block' }} />
                </Link>
              )}
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#2c5282', margin: '0 0 8px 0' }}>{b.title}</h3>
                {b.summary && (
                  <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6, maxHeight: 48, overflow: 'hidden' }}>{b.summary}</p>
                )}
                <div style={{ marginTop: 10 }}>
                  <Link to={`/blogs/${b.blogID}`} style={{ color: '#2c5282', fontWeight: 700, textDecoration: 'none' }}>Xem bài viết</Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* External RSS items */}
        <h2 style={{ color: '#2c5282', borderLeft: '6px solid #2c5282', paddingLeft: 15, margin: '40px 0 25px', fontWeight: 800 }}>📰 Bản Tin Y Tế Cho Ông Bà (Bộ Y Tế)</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 25, marginBottom: 60 }}>
	          {externalBlogs.map((b, index) => (
            <article key={`external-${b.key}`} style={{ background: '#fff', borderRadius: 15, overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%' }}>
              {b.image && (
                <Link to={`/blogs/external?link=${encodeURIComponent(b.link)}`} style={{ display: 'block' }}>
	                  <img src={resolveImg(b.image)} alt={b.title} loading={index < 6 ? 'eager' : 'lazy'} decoding="async" fetchPriority={index < 3 ? 'high' : 'low'} style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block', background: '#eee' }}
                       onError={(e) => {
                         const img = e.currentTarget;
                         if (img.dataset.fallbackApplied === '1') return; // avoid loops/log spam
                         img.dataset.fallbackApplied = '1';
	                         img.src = BLOG_IMAGE_PLACEHOLDER;
	                       }} />
                </Link>
              )}
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h3 style={{ fontSize: 17, color: '#1a365d', height: 48, overflow: 'hidden', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                  <Link to={`/blogs/external?link=${encodeURIComponent(b.link)}`} style={{ color: '#1a365d', textDecoration: 'none', fontWeight: 700 }}>{b.title}</Link>
                </h3>
                {b.summary && (
                  <p style={{ fontSize: 14, color: '#64748b', height: 60, overflow: 'hidden', margin: 0, lineHeight: 1.6 }}>{b.summary}</p>
                )}
                <div style={{ marginTop: 'auto' }}>
                  <Link to={`/blogs/external?link=${encodeURIComponent(b.link)}`} className="btn-link" style={{ background: '#2c5282', color: 'white', padding: 10, borderRadius: 5, textDecoration: 'none', fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Đọc tại TamCare
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {!loading && !error && internalBlogs.length === 0 && externalBlogs.length === 0 && (
          <div className="tamcare-alert">Chưa có bài viết.</div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
